import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { User, Key, Eye, EyeSlash, FloppyDisk } from "@phosphor-icons/react";

export function ProfileTab() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const handleSaveName = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await api.put("/auth/me", { name: name.trim() });
      toast.success("Profile updated");
      if (checkAuth) await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
      toast.success("Password changed successfully");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Password change failed");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Profile Info */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <User size={16} weight="bold" className="text-[#007AFF]" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Username</Label>
              <Input value={user?.username || ""} disabled
                className="bg-[#0A0A0A] border-[#262626] text-[#737373] rounded-sm font-mono-data" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Role</Label>
              <Input value={user?.role || ""} disabled
                className="bg-[#0A0A0A] border-[#262626] text-[#737373] rounded-sm uppercase" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Location</Label>
              <Input value={user?.location || ""} disabled
                className="bg-[#0A0A0A] border-[#262626] text-[#737373] rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Display Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="profile-name-input" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSaveName} disabled={savingName || name === user?.name}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="profile-save-name-btn">
              <FloppyDisk size={14} className="mr-1" /> {savingName ? "Saving..." : "Save Name"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Key size={16} weight="bold" className="text-[#FACC15]" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#737373]">For your security, please re-enter your current password to authorise the change.</p>
          <div className="space-y-3 max-w-md">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Current Password</Label>
              <div className="flex items-center gap-2">
                <Input type={showPwd ? "text" : "password"} value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
                  placeholder="Enter your current password" autoComplete="current-password"
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm flex-1" data-testid="profile-current-pwd" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="p-2 text-[#737373] hover:text-white" data-testid="profile-toggle-pwd">
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">New Password</Label>
              <Input type={showPwd ? "text" : "password"} value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                placeholder="At least 6 characters" autoComplete="new-password" data-lpignore="true" name="profile-new-pw"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="profile-new-pwd" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Confirm New Password</Label>
              <Input type={showPwd ? "text" : "password"} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Re-enter new password" autoComplete="new-password" data-lpignore="true" name="profile-confirm-pw"
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="profile-confirm-pwd" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleChangePassword} disabled={savingPwd || !currentPwd || !newPwd || !confirmPwd}
              className="bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-sm text-xs font-semibold" data-testid="profile-change-pwd-btn">
              <Key size={14} className="mr-1" /> {savingPwd ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
